import {defineStore} from 'pinia'
import {Platform} from 'quasar'
import appEventBusService from '../services/utils/appEventBusService';
import BusEnum from '@core/enums/BusEnum';

const name = 'layout'

const layoutStore = JSON.parse(localStorage.getItem("layout.store"));

const initialState = layoutStore ? layoutStore : {
  page_title: "",
  page_subtitle: "",
  show_reverse_nav: false,
  searchFilter: "",
  canShowFilter: false,
  hideChatButton: false,
  left_side_state: false,
  screenOrientation: 'portrait',
  is_dark: false,
  language: 'fr',
  currentProductId: undefined,
  menuState: 'expanded', // 'expanded', 'collapsed', or 'hidden'
  miniState : false,
  drawerWidth: 275
}

// Force the drawer closed on mobile at boot — even when localStorage carries
// over a `left_side_state: true` from a previous desktop session. Otherwise
// the user lands on a phone with a sidebar covering the page content. The
// desktop preference is preserved by NOT writing back to storage here.
if (Platform.is.mobile) {
  initialState.left_side_state = false;
  initialState.menuState = 'hidden';
}

// Nombre de bannières (hero) montées sur la page courante : portant leur propre bouton
// « épingler » en mobile, l'en-tête retire alors le sien. Jamais persisté.
initialState.hero_count = 0;
// Titre affiché par l'en-tête (appHeader) : les bannières (heroes) masquent leur titre s'il est identique
initialState.header_title = '';
// Total / compteur de la page (prop `total` des bannières), affiché à droite de l'en-tête. Jamais persisté.
initialState.header_total = '';
// Export de la grille de la page (bouton « télécharger » à droite de l'en-tête) : fonction, jamais persistée.
initialState.header_export = null;


export const useLayoutStore = defineStore(name, {

  id: name,

  state: () => (initialState),

  actions: {
    setDrawerWidth(w) {
      this.drawerWidth = Math.max(200, Math.min(w, window.innerWidth * 0.4));
      this.updateStorage();
    },
    setMiniState(state) {
      this.miniState = state;
      this.updateMenuState()
    },
    updateMenuState() {
      if( this.left_side_state) {
        if(this.miniState) {
          this.menuState = 'collapsed'
        }
        else {
          this.menuState = 'expanded'
        }
      }
      else {
        this.menuState = 'hidden'

      }
    },
    setCurrentProductId(id) {
      this.currentProductId = id
      this.updateStorage()
    },
    setBackRoute(options) {
      this.back_route = options
    },
    resetBackRoute() {
      this.back_route = undefined
    },
    setPageTitle(title) {
      this.page_title = title
    },
    setHeaderTitle(title) {
      this.header_title = title || ''
    },
    /** Grille de la page : fonction d'export appelée par le bouton « télécharger » de l'en-tête */
    setHeaderExport(fn) {
      this.header_export = typeof fn === 'function' ? fn : null
    },
    /** Retire l'export de l'en-tête seulement s'il appartient à l'appelant */
    clearHeaderExport(fn) {
      if (!fn || this.header_export === fn) this.header_export = null
    },
    setHeaderTotal(total) {
      this.header_total = (total === null || total === undefined) ? '' : String(total)
    },
    registerHero() {
      this.hero_count = (this.hero_count || 0) + 1
    },
    unregisterHero() {
      this.hero_count = Math.max(0, (this.hero_count || 0) - 1)
    },
    setPageSubTitle(subtitle) {
      this.page_subtitle = subtitle
    },
    setShowReverseNav(show) {
      this.show_reverse_nav = show
    },
    /**
     * Register a custom handler invoked when the user clicks the back arrow
     * in the appHeader (only active while `show_reverse_nav === true`).
     *
     * The handler receives no argument and must return :
     *   - `true`  → the page has handled the back action itself (e.g. it
     *               collapsed an inner step, closed a modal, etc.) ; the
     *               header DOES NOT call `router.back()`.
     *   - `false` (or anything not strictly `true`) → fall back to the
     *               default `NavigationService.goBack()` behaviour.
     *
     * Pages MUST call {@link clearBackHandler} in their `beforeUnmount`
     * hook to avoid leaking a stale closure across route changes.
     *
     * NB: not persisted to localStorage (functions aren't JSON-serializable).
     */
    setBackHandler(fn) {
      this.back_handler = typeof fn === 'function' ? fn : null
    },
    clearBackHandler() {
      this.back_handler = null
    },
    setSearchFilter(searchTxt) {
      this.searchFilter = searchTxt
    },
    setCanShowFilter(flag) {
      this.canShowFilter = flag
    },
    /** Masque le bouton flottant de C'lia (page qui a son propre bouton en bas d'écran) ; non persisté. */
    setHideChatButton(flag) {
      this.hideChatButton = !!flag
    },
    toggleLeftSideState() {
      this.left_side_state = !this.left_side_state;
      this.updateMenuState()
    },
    setLeftSideState(val) {
      this.left_side_state = !!val;
      this.updateMenuState()
    },
    toggleDarkMode() {
      this.is_dark = !this.is_dark;
      this.updateStorage()
      appEventBusService.emit(BusEnum.THEME_UPDATE, this.is_dark)
    },
    updateLanguage(lang) {
      this.language = lang
      this.updateStorage()
    },
    updateStorage() {
      let layout = {
        page_title: this.page_title,
        page_subtitle: this.page_subtitle,
        show_reverse_nav: this.show_reverse_nav,
        searchFilter: this.searchFilter,
        canShowFilter: this.canShowFilter,
        left_side_state: this.left_side_state,
        is_dark: this.is_dark,
        language: this.language,
        currentProductId: this.currentProductId,
        drawerWidth: this.drawerWidth
      }

      localStorage.setItem("layout.store", JSON.stringify(layout));
    },

  },
  getters: {
    getCurrentProductId: (state) => {
      return state.currentProductId
    },
    getBackRoute: (state) => {
      return state.back_route
    },
    getMenuState: (state) => {
      return state.menuState
    },
    darkStyle: (state) => {
      return state.is_dark ? 'background:white' : 'background:black'
    },
    isDarkMode: (state) => {
      return state.is_dark
    },
    getPageTitle: (state) => {
      return state.page_title
    },
    getHeaderTitle: (state) => state.header_title || '',
    getHeaderTotal: (state) => state.header_total || '',
    getHeaderExport: (state) => state.header_export || null,
    hasHero: (state) => {
      return (state.hero_count || 0) > 0
    },
    getPageSubTitle: (state) => {
      return state.page_subtitle
    },
    getShowReverseNav: (state) => {
      return state.show_reverse_nav
    },
    getBackHandler: (state) => {
      return state.back_handler
    },
    getSearchFilter: (state) => {
      return state.searchFilter
    },
    getCanShowFilter: (state) => {
      return state.canShowFilter
    },
    getLeftSideState: (state) => {
      return state.left_side_state
    },
    getDrawerWidth: (state) => {
      return state.drawerWidth || 275
    },
    isMobile: () => {
      let condition = false
      if (Platform.is.mobile !== undefined) {
        condition = Platform.is.mobile === true
      }
      return condition
    },
    isDesktop: () => {
      let condition = false
      if (Platform.is.desktop !== undefined) {
        condition = Platform.is.desktop === true
      }
      return condition
    }
  }

})
